import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@ibe/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { queryKeys } from '../../lib/api/query-keys.js';
import { ImportPreview } from './import-preview.js';
import { useImportCommit, useImportDryRun } from '../../lib/resource/import/use-import.js';
import { type ImportReport } from '../../modules/reference/api/schemas.js';

/**
 * Upload, review, apply — one dialog for every module that imports.
 *
 * Nothing here is per-entity except the base path it posts to and the template
 * it links, which is why it lives in `lib/resource` rather than in a module.
 *
 * A reviewed plan is work the operator did: closing with one uncommitted asks
 * first, for the same reason `useUnsavedGuard` protects a typed-in form.
 */
export interface ImportDialogProps {
  readonly resource: keyof typeof queryKeys;
  readonly basePath: string;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Injected by tests. Production uses the hooks. */
  readonly onDryRun?: (file: File) => Promise<ImportReport>;
  readonly onCommit?: (importId: string) => Promise<{ created: number; updated: number }>;
}

export function ImportDialog({
  resource,
  basePath,
  open,
  onClose,
  onDryRun,
  onCommit,
}: ImportDialogProps): React.JSX.Element {
  const [report, setReport] = useState<ImportReport | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const dryRun = useImportDryRun(basePath);
  const commit = useImportCommit(basePath);
  const client = useQueryClient();

  const run = onDryRun ?? ((file: File) => dryRun.mutateAsync(file));
  const apply = onCommit ?? ((importId: string) => commit.mutateAsync(importId));

  const canApply = report !== null && report.errors.length === 0;
  const hasUnappliedWork = report !== null;

  const close = (): void => {
    if (hasUnappliedWork && !confirmingClose) {
      setConfirmingClose(true);
      return;
    }
    setReport(null);
    setConfirmingClose(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import from a file</DialogTitle>
          <DialogDescription>
            Upload a CSV or JSON file. Nothing is written until you have seen what it would do.{' '}
            <a className="underline" href={`${basePath}/import/template`} download>
              Download the template
            </a>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium" htmlFor="import-file">
              File
            </label>
            <input
              id="import-file"
              type="file"
              accept=".csv,.json,text/csv,application/json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file === undefined) return;
                void run(file).then(setReport);
              }}
            />
          </div>

          {report !== null && <ImportPreview report={report} />}

          {confirmingClose && (
            <p className="text-sm text-destructive">
              This import has not been applied. Close anyway?
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={close}>
              {confirmingClose ? 'Close anyway' : 'Cancel'}
            </Button>
            <Button
              type="button"
              disabled={!canApply}
              onClick={() => {
                if (report === null) return;
                void apply(report.importId).then(async () => {
                  // Both roots: the module's own list and the shared vocabulary
                  // every other form's picker reads. An import that changed 250
                  // countries and left the property form's picker stale is an
                  // import that half worked.
                  await client.invalidateQueries({ queryKey: [resource] });
                  await client.invalidateQueries({ queryKey: ['reference'] });
                  setReport(null);
                  setConfirmingClose(false);
                  onClose();
                });
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
